import {
  Home,
  Users,
  CreditCard,
  Shield,
  Settings,
  Activity,
  LogOut,
  FileText,
  BrainCircuit,
  BarChart3,
  FlaskConical,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

interface SidebarProps {
  userRole: string; // 'SUPERADMIN', 'OWNER', 'ADMIN', 'USER'
  onLogout: () => void;
}

export default function Sidebar({ userRole, onLogout }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Define menus based on roles
  const menuItems = [
    { id: "/dashboard", label: "Dashboard", icon: Home, roles: ["ALL"] },

    // --- AI FEATURE ---
    {
      id: "/upload",
      label: "Smart Import",
      icon: BrainCircuit,
      roles: ["OWNER", "ADMIN", "USER", "SUPERADMIN"],
      badge: "AI",
    },
    // ----------------------

    // ✅ NEW: Users Management (OWNER/ADMIN only)
    {
      id: "/users",
      label: "Users",
      icon: Users,
      roles: ["OWNER", "ADMIN"],
    },

    {
      id: "/wallet",
      label: "Wallet & Billing",
      icon: CreditCard,
      roles: ["OWNER", "ADMIN"],
    },
    {
      id: "/rules",
      label: "Law Library",
      icon: Shield,
      roles: ["OWNER", "ADMIN", "SUPERADMIN"],
    },
    {
      id: "/reports",
      label: "Reports",
      icon: BarChart3,
      roles: ["ALL"],
    },
    {
      id: "/admin/orgs",
      label: "Organizations",
      icon: FileText,
      roles: ["SUPERADMIN"],
    },
    {
      id: "/admin/system",
      label: "System Health",
      icon: Activity,
      roles: ["SUPERADMIN"],
    },
    {
      id: "/admin/grounding-lab",
      label: "Grounding Lab",
      icon: FlaskConical,
      roles: ["SUPERADMIN"],
      badge: "LAB",
    },
    { id: "/settings", label: "Settings", icon: Settings, roles: ["ALL"] },
  ];

  return (
    <div
      className={`
        w-64 h-screen flex flex-col fixed left-0 top-0 z-20 transition-colors duration-200 border-r
        bg-white border-gray-200 
        dark:bg-slate-900 dark:border-slate-800
      `}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-800">
        <h1 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
          <Shield className="text-blue-600 dark:text-blue-500" />
          iRedact
        </h1>
        <p className="text-xs mt-1 text-gray-500 dark:text-slate-500">
          {userRole === "SUPERADMIN" ? "God Mode" : "Compliance Portal"}
        </p>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          // Filter logic
          if (!item.roles.includes("ALL") && !item.roles.includes(userRole)) {
            return null;
          }

          // ✅ Better active logic for nested routes
          const isActive =
            location.pathname === item.id ||
            location.pathname.startsWith(item.id + "/");

          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.id)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group
                ${
                  isActive
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20 dark:shadow-blue-900/20"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                }
              `}
            >
              <Icon
                size={18}
                className={
                  isActive
                    ? "text-white"
                    : "text-gray-400 group-hover:text-blue-600 dark:text-slate-500 dark:group-hover:text-blue-400 transition-colors"
                }
              />

              <span>{item.label}</span>

              {/* BADGE (AI) */}
              {/* @ts-ignore */}
              {item.badge && !isActive && (
                <span
                  className={`
                    ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border
                    bg-blue-50 text-blue-600 border-blue-100
                    dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/20
                  `}
                >
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-slate-800">
        <button
          onClick={onLogout}
          className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm font-medium
            text-gray-500 hover:bg-red-50 hover:text-red-600
            dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-red-400
          `}
        >
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
}
